/**
 * member-selector.js
 */

import React from 'react';
import { useMediaQuery, Tooltip, Button, Popover, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Autocomplete } from '@mui/material';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import sortMembers from './sort-members';
import GroupMember from './group-member';


const useStyles = makeStyles(theme => ({
  popperRoot: {
    transition: `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
    backgroundColor: 'transparent',
    '&.fade': {
      backgroundColor: 'rgba(0,0,0,0.3)'
    }
  },
  popper: {
    width: theme.spacing(40),
    '& .MuiPaper-root': {
      margin: 0
    },
    '& .MuiAutocomplete-option': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiListItem-root': {
      paddingRight: 0,
    },
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100% - 32px)',
      display: 'flex',
      flexDirection: 'column',
      '& .MuiAutocomplete-popper': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      },
      '& .MuiAutocomplete-paper': {
        display: 'flex',
        flexDirection: 'column',
      },
      '& .MuiAutocomplete-listbox': {
        flex: 1,
        maxHeight: 'inherit',
      }
    }
  },
  popperDisablePortal: {
    position: 'relative !important',
    width: 'auto !important',
    transform: 'unset !important',
  },
  acInput: {
    width: '100%',
    '& .MuiInputBase-root': {
      paddingRight: 0
    }
  },
  closeButton: {
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
}));


export default function MemberSelector(props) {
  const {
    options,
    members,
    onInputChange,
    filterOptions,
    onAdd,
    disabledReason
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext)
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const [anchor, setAnchor] = React.useState();
  const [filterText, setFilterText] = React.useState('');
  const [availableOptions, setAvailableOptions] = React.useState([]);
  const [fade, setFade] = React.useState(false);


  React.useEffect(() => {
    if (options && members) {
      const memberIds = members.map(m => m.id);
      setAvailableOptions(options
        .filter(m => !memberIds.includes(m.id))
        .sort(sortMembers(user)));
    }
  }, [options, members, user]);


  React.useEffect(() => {
    setTimeout(() => setFade(Boolean(anchor)));
  }, [anchor]);


  const onClosePopper = (event, reason) => {
    if (reason === 'toggleInput') {
      return;
    }
    setAnchor();
  }


  return <>
    <Tooltip
      arrow
      placement='top'
      title={disabledReason || ''}
    >
      <span>
        <Button
          variant='outlined'
          color='primary'
          disabled={Boolean(disabledReason)}
          startIcon={<Icon type='group-add-members' />}
          children={<>&nbsp;Add Members</>}
          onClick={(e) => {
            if (isMobile) {
              setAnchor({ position: { top: 0, left: 0 } })
            } else {
              setAnchor({ element: e.currentTarget })
            }
          }}
        />
      </span>
    </Tooltip>


    <Popover
      id='auto-complete-popper'
      open={Boolean(anchor)}
      anchorReference={anchor?.position ? 'anchorPosition' : 'anchorEl'}
      anchorEl={anchor?.element}
      anchorPosition={anchor?.position}
      classes={{ root: `${classes.popperRoot} ${fade ? 'fade' : ''}`, paper: classes.popper }}
    >
      <Autocomplete
        open
        freeSolo
        autoHighlight
        disableClearable
        disableCloseOnSelect
        disablePortal
        classes={{ popperDisablePortal: classes.popperDisablePortal }}
        options={availableOptions}
        filterOptions={filterOptions}
        onClose={onClosePopper}
        onChange={async (e, v, r) => {
          if ((await onAdd(v)) && v.doClose) {
            setAnchor();
          }
        }}
        getOptionLabel={(option) => `${option?.name} ${option?.email}`}
        getOptionDisabled={(option) => Boolean(option.disabled)}
        inputValue={filterText}
        onInputChange={(e, v, r) => {
          if (!(r === 'reset' && v !== '')) {
            setFilterText(v);
            if (onInputChange) {
              onInputChange(v);
            }
          }
        }}
        renderInput={(params) => {
          const { InputProps, ...rest } = params;
          return <TextField
            {...rest}
            variant='outlined'
            // size={isMobile ? 'medium' : 'small'}
            placeholder='Search by Name or Email'
            className={classes.acInput}
            ref={InputProps.ref}            
            autoFocus
            InputProps={{
              ...InputProps,
              endAdornment: (
                <Button
                  className={classes.closeButton}
                  onClick={onClosePopper}
                  children='Close'
                />
              )
            }}
          />
        }}
        renderOption={(liProps, member) => <li {...liProps} key={member.id}>
          {member.id === 'error'
            ? <>{member.name}</>
            : <GroupMember 
              component='div' 
              member={member} 
              initials={member.id === 'request' ? <Icon type='group-add-members' /> : undefined} 
              highlight={member.id === 'request'}
            />
          }
        </li>}
      />
    </Popover>
  </>;
}